import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { EmojiSearch, PickerModule } from '@ctrl/ngx-emoji-mart';
import { Observable, map } from 'rxjs';

import { MensagemModel } from '../../../../shared/model/chat/mensagens/mensagem-model';
import { AuthService } from '../../../../shared/service/auth.service';
import { UsuarioService } from '../../../../shared/service/usuario.service';
import { animacaoExpandir } from '../../../utils/chat-animacoes.utils';
import { EmojiBuscaPtService } from '../../../../shared/service/emoji-busca-pt.service';
import { I18N_EMOJI } from '../../../utils/i18n-emoji.constantes';
import { ModoMenuReacao } from './models/modo-menu-reacao.model';
import { REACOES_RAPIDAS } from './utils/menu-reacao.constantes';
import { MethodsUtils } from '../../../../shared/util/methods.utils';

@Component({
  selector: 'app-menu-reacao',
  standalone: true,
  imports: [PickerModule],
  providers: [{ provide: EmojiSearch, useClass: EmojiBuscaPtService }],
  templateUrl: './menu-reacao.component.html',
  styleUrl: './menu-reacao.component.css',
  animations: [animacaoExpandir]
})
export class MenuReacaoComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input({ required: true }) mensagem!: MensagemModel;
  @Input() modo: ModoMenuReacao = 'seletor';
  @Input() podeReagir = false;
  @Input() nomeContato = '';
  @Input() fotoContato = '';

  @Output() escolher = new EventEmitter<string>();
  @Output() remover = new EventEmitter<void>();
  @Output() redimensionou = new EventEmitter<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private observador?: ResizeObserver;
  private quadroRedimensionar: number | null = null;

  protected readonly reacoesRapidas = REACOES_RAPIDAS;
  protected readonly i18nEmoji = I18N_EMOJI;
  seletorCompleto = signal(false);
  modoAtual = signal<ModoMenuReacao>('seletor');

  private readonly usuario = inject(AuthService).getUsuarioLogadoClariBot();
  private readonly usuarioService = inject(UsuarioService);
  fotoPropria = signal<string | null>(null);

  ngOnInit(): void {
    this.modoAtual.set(this.modo);
    if (this.modo === 'detalhes' && this.reacaoEhMinha && !this.mensagem.reacaoUsuarioUuid) {
      this.buscarFotoPropria().subscribe(foto => this.fotoPropria.set(foto));
    }
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observador = new ResizeObserver(() => {
        if (this.quadroRedimensionar !== null) return;
        this.quadroRedimensionar = requestAnimationFrame(() => {
          this.quadroRedimensionar = null;
          this.redimensionou.emit();
        });
      });
      this.observador.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.observador?.disconnect();
    if (this.quadroRedimensionar !== null) cancelAnimationFrame(this.quadroRedimensionar);
  }

  private buscarFotoPropria(): Observable<string | null> {
    return this.usuarioService.buscarUsuarioLogadoEmCache().pipe(
      map(u => (u?.uuid && u?.extensaoImg ? this.montarUrlFoto(u.uuid, u.extensaoImg) : null))
    );
  }

  private montarUrlFoto(uuid: string, extensao: string): string {
    return MethodsUtils.urlFotoUsuario(this.usuario?.grupo, uuid, extensao);
  }

  get reacaoEhDoOperador(): boolean {
    return this.mensagem.sendBy === 'CLIENTE';
  }

  get reacaoEhMinha(): boolean {
    if (!this.reacaoEhDoOperador) return false;
    return this.mensagem.reacaoUsuarioId == null || Number(this.mensagem.reacaoUsuarioId) === Number(this.usuario?.id);
  }

  get nomeAutor(): string {
    if (!this.reacaoEhDoOperador) return this.nomeContato || 'Cliente';
    if (this.reacaoEhMinha) return 'Você';
    return this.mensagem.reacaoUsuarioNome || 'Operador';
  }

  get fotoAutor(): string | null {
    if (!this.reacaoEhDoOperador) return this.fotoContato || null;
    const { reacaoUsuarioUuid: uuid, reacaoUsuarioExtensaoImg: extensao } = this.mensagem;
    if (uuid && extensao) return this.montarUrlFoto(uuid, extensao);
    return this.reacaoEhMinha ? this.fotoPropria() : null;
  }

  readonly fotoComErro = signal(false);

  get inicialAutor(): string {
    return (this.nomeAutor || '?').trim().charAt(0).toUpperCase();
  }

  escolherRapida(emoji: string): void {
    this.escolher.emit(emoji);
  }

  escolherCompleto(event: any): void {
    const emoji: string | undefined = event?.emoji?.native;
    if (emoji) this.escolher.emit(emoji);
  }

  abrirSeletor(): void {
    this.modoAtual.set('seletor');
  }

  clicarAutor(): void {
    if (this.reacaoEhMinha) this.remover.emit();
  }
}
